import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import Card from '@/components/shared/Card';
import { apiFetch } from '@/lib/api';
import {
  FiCheck, FiPhone, FiClock, FiShield, FiAlertCircle, FiZap, FiTrendingDown,
  FiCalendar, FiFileText, FiChevronDown, FiAward, FiActivity, FiUsers,
  FiBarChart2, FiRefreshCw, FiX, FiArrowRight, FiThumbsUp, FiEdit3, FiDownload,
} from 'react-icons/fi';

const TEAL = '#0d9488';

// Kundentaugliche Fehlermeldung aus der Server-Antwort lesen.
// Technische Details (Stacktraces, SQL, Statuscodes) werden bewusst verworfen —
// der Kunde bekommt in dem Fall unsere eigene, freundliche Meldung.
const serverHinweis = async (resp) => {
  let msg = '';
  try {
    const data = await resp.json();
    if (typeof data?.error === 'string') msg = data.error.trim();
  } catch { /* Antwort ohne verwertbares JSON */ }
  if (!msg || resp.status >= 500) return null;
  const wirktTechnisch = msg.length > 140
    || /[{}<>;]/.test(msg)
    || /\b(error|exception|stack|sql|syntax|relation|column|constraint|undefined|null|econn|etimedout|timeout)\b/i.test(msg);
  return wirktTechnisch ? null : msg;
};

const NETZ_HINWEIS = 'Wir konnten unseren Server gerade nicht erreichen. Bitte pruefen Sie kurz Ihre Internetverbindung und senden Sie erneut — Ihre Eingaben bleiben erhalten.';

const fmtEUR = (n) => n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
const fmtEUR2 = (n) => n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

// ct/kWh-Ausgabe. Fehlt der Wert (oder ist er unlesbar), erscheint '–' — nie eine 0.
// NUMERIC-Spalten liefern ueber JSON teils Strings, deshalb die Umwandlung.
const zahl = (v) => {
  const n = typeof v === 'string' ? Number(v) : v;
  return (n == null || typeof n !== 'number' || Number.isNaN(n)) ? null : n;
};
const ctText = (v, nk = 2) => {
  const n = zahl(v);
  return n == null ? '–' : n.toFixed(nk);
};

// ============================================================
// Preisbasis: alte Angebote (Komplettpreis) vs. neue (Energiepreis)
// ============================================================
// Bereits versendete Angebote liegen als JSONB-Schnappschuss auf dem Server und
// tragen ihre alten Felder. Sie muessen aussehen wie am Tag des Versands — der
// Modus wird deshalb aus den DATEN des Angebots gelesen, nie aus dem Code-Stand.
//   neu: 'preisbasis' === 'energie' oder eine Laufzeit fuehrt angebotspreis_ct_kwh
//   alt: alles andere → unveraenderte Komplettpreis-Darstellung
const istEnergiebasis = (k) =>
  k?.preisbasis === 'energie'
  || (k?.results || []).some(r => (r?.laufzeiten || []).some(l => l?.angebotspreis_ct_kwh != null));

// Steht in der neuen Darstellung an JEDER Preisangabe — nicht nur im Kleingedruckten.
const ZUSATZ = 'zzgl. Netzentgelte, Abgaben und Umlagen';

// Energiepreis = Beschaffung + AIQCON-Verguetung, also der Angebotspreis.
// Reihenfolge der Quellen, bewusst so:
//   1. angebotspreis_ct_kwh — der von der Kalkulation ausgewiesene Wert.
//   2. Komplettpreis minus Netzentgelt minus Abgaben. Diese Identitaet der
//      Kalkulation bleibt richtig, gleichgueltig ob die Provision im
//      Energieanteil steckt oder als eigene Position danebensteht.
//   3. Energie + Provision.
// Traegt der Datensatz nichts davon, gibt es KEINEN Ersatzwert — dann '–'.
const energiepreisVon = (lz) => {
  if (!lz) return null;
  if (lz.angebotspreis_ct_kwh != null) return zahl(lz.angebotspreis_ct_kwh);
  const komplett = zahl(lz.komplettpreis_ct_kwh);
  const netz = zahl(lz.netzentgelt_ct_kwh);
  const abgaben = zahl(lz.abgaben_ct_kwh);
  if (komplett != null && netz != null && abgaben != null) {
    const rest = Math.round((komplett - netz - abgaben) * 10000) / 10000;
    if (rest > 0) return rest;
  }
  const energie = zahl(lz.energie_ct_kwh);
  const provision = zahl(lz.provision_ct_kwh);
  if (energie == null || provision == null) return null;
  return Math.round((energie + provision) * 10000) / 10000;
};

// Beschriftung der einzelnen Abgaben (Reihenfolge wie in abgabenUmlagen.js)
const ABGABEN_LABEL = {
  stromsteuer: 'Stromsteuer',
  offshore_umlage: 'Offshore-Netzumlage',
  kwkg_umlage: 'KWKG-Umlage',
  stromnev_umlage: '§19-StromNEV-Umlage',
  konzessionsabgabe: 'Konzessionsabgabe',
};

// ============================================================
// Preis-Options-Karte (waehlbar)
// ============================================================
const OptionCard = ({ option, preis, jahrEur, energiebasis, isSelected, isBest, onSelect }) => {
  const jahre = Math.round(option.laufzeit_monate / 12);
  return (
    <button
      onClick={() => onSelect(option.laufzeit_monate)}
      className={`relative text-left rounded-xl border-2 p-5 transition-all ${
        isSelected ? 'border-teal-500 bg-teal-50/40 shadow-lg ring-2 ring-teal-200' : 'border-gray-200 bg-white hover:border-teal-300'
      }`}
    >
      {isBest && (
        <span className="absolute -top-3 left-5 bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
          Empfehlung
        </span>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm text-gray-500">Laufzeit</p>
          <p className="text-lg font-bold text-gray-900">{jahre} {jahre > 1 ? 'Jahre' : 'Jahr'}</p>
          <p className="text-xs text-gray-400">bis {option.lieferende_display}</p>
        </div>
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-teal-600 bg-teal-600' : 'border-gray-300'}`}>
          {isSelected && <FiCheck className="text-white" size={14} />}
        </div>
      </div>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className={`text-3xl font-bold ${isSelected ? 'text-teal-600' : 'text-gray-900'}`}>
            {ctText(preis)}
          </p>
          <p className="text-xs text-gray-500">ct/kWh netto{energiebasis ? ' — Energiepreis' : ''}</p>
          {energiebasis && <p className="text-[11px] text-gray-500 leading-snug mt-1">{ZUSATZ}</p>}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-bold text-gray-900">{fmtEUR(jahrEur)}</p>
          <p className="text-xs text-gray-400">{energiebasis ? 'Energie pro Jahr' : 'pro Jahr'}</p>
        </div>
      </div>
    </button>
  );
};

// ============================================================
// FAQ-Accordion
// ============================================================
const faqItems = (energiebasis, istRLM = false) => [
  { q: 'Kostet mich Ihr Service etwas?', a: 'Nein. Unsere Beratung und der Wechselservice sind fuer Sie komplett kostenfrei. Wir werden ueber die Versorger verguetet — fuer Sie entstehen keine Gebuehren.' },
  { q: 'Bin ich durch dieses Angebot schon gebunden?', a: 'Nein. Mit Ihrer Bestaetigung geben Sie uns lediglich den Auftrag, fuer Sie verbindliche Angebote einzuholen. Erst wenn Sie einen konkreten Vertrag final unterschreiben, entsteht eine Bindung.' },
  energiebasis
    ? { q: 'Wie verbindlich ist der angezeigte Energiepreis?', a: 'Der Energiepreis basiert auf den aktuellen Boersennotierungen (EEX) zzgl. transparenter Strukturkosten und gibt eine realistische Preisspanne wieder. Den final verbindlichen Preis bestaetigt der gewaehlte Versorger im Vertrag. Netzentgelte, Abgaben und Umlagen sind darin nicht enthalten — sie kommen hinzu und werden vom Netzbetreiber bzw. vom Gesetzgeber festgesetzt.' }
    : { q: 'Wie verbindlich ist der angezeigte Preis?', a: 'Die Preise basieren auf den aktuellen Boersennotierungen (EEX) zzgl. transparenter Strukturkosten und geben eine realistische Preisspanne wieder. Netzentgelte sowie Abgaben und Umlagen sind mit den fuer Ihr Netzgebiet vorliegenden Werten kalkuliert; sie werden vom Netzbetreiber bzw. vom Gesetzgeber festgesetzt und jaehrlich angepasst. Den final verbindlichen Preis bestaetigt der gewaehlte Versorger im Vertrag.' },
  ...(energiebasis ? [
    { q: 'Warum enthaelt der Preis keine Netzentgelte und Abgaben?', a: 'Netzentgelte zahlt jeder Stromkunde an den Betreiber des oertlichen Netzes; Stromsteuer, KWKG-Umlage, Offshore-Netzumlage, §19-StromNEV-Umlage und Konzessionsabgabe sind gesetzlich festgelegt. Beides faellt bei jedem Versorger in identischer Hoehe an und ist nicht verhandelbar. Wir weisen deshalb genau den Teil aus, den wir fuer Sie verhandeln — so koennen Sie Angebote sauber miteinander vergleichen.' },
    istRLM
      ? { q: 'Was zahle ich am Ende insgesamt?', a: 'Ihre Stromkosten bestehen aus dem Energiepreis dieses Angebots und der Netznutzung samt Abgaben und Umlagen. Die Netznutzung rechnet Ihr Netzbetreiber ueber Leistungs- und Arbeitspreis ab; die genauen Saetze stehen im Preisblatt Ihres Netzbetreibers. Sie sind reguliert, bei jedem Versorger gleich hoch und werden unveraendert weitergegeben. Bei registrierender Leistungsmessung ist es deshalb branchenueblich, den Energiepreis zu vergleichen und keinen Gesamtpreis zu quotieren — wir nennen hier bewusst keine geschaetzte Gesamtzahl.' }
      : { q: 'Was zahle ich am Ende insgesamt?', a: 'Ihr Gesamtpreis ist der Energiepreis aus diesem Angebot plus Netzentgelte, Abgaben und Umlagen. Die derzeit fuer Ihr Netzgebiet vorliegenden Werte finden Sie in diesem Angebot im Abschnitt „Netzentgelte, Abgaben und Umlagen“. Sie werden jaehrlich neu festgesetzt und sind deshalb nicht Teil unserer Preiszusage.' },
  ] : []),
  { q: 'Was passiert mit meinem bestehenden Vertrag?', a: 'Wir uebernehmen auf Wunsch die fristgerechte Kuendigung Ihres alten Vertrags und sorgen fuer einen lueckenlosen Lieferantenwechsel — ohne Versorgungsunterbrechung.' },
  { q: 'Wie schnell geht der Wechsel?', a: 'Nach Ihrer Beauftragung holen wir die verbindlichen Angebote ein (i.d.R. wenige Werktage). Der eigentliche Lieferantenwechsel erfolgt zum gewuenschten Lieferbeginn.' },
  { q: 'Warum sind Sie unabhaengig?', a: 'Wir sind kein Versorger, sondern Ihr unabhaengiger Energiemakler. Wir vergleichen den Markt und waehlen das fuer Sie beste Angebot — herstellerneutral.' },
];

const FaqAccordion = ({ items }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="bg-white rounded-lg border overflow-hidden">
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
          >
            <span className="font-medium text-gray-900 text-sm">{item.q}</span>
            <FiChevronDown className={`text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`} size={18} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{item.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// Signatur-Pad (Canvas, Maus/Finger)
// ============================================================
const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current; const ctx = c.getContext('2d');
    const p = pos(e);
    ctx.strokeStyle = '#111827'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height);
    onChange(null);
  };

  return (
    <div>
      <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 relative">
        <canvas
          ref={canvasRef} width={500} height={150}
          className="w-full touch-none cursor-crosshair"
          style={{ height: 150 }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <span className="absolute bottom-2 left-3 text-xs text-gray-300 pointer-events-none">Hier unterschreiben</span>
      </div>
      <button type="button" onClick={clear} className="text-xs text-gray-500 hover:text-gray-700 mt-1">Loeschen</button>
    </div>
  );
};

// ============================================================
// Vollmacht-Dialog
// ============================================================
const VollmachtDialog = ({ vForm, setVForm, signature, setSignature, typedName, setTypedName, vAgree, setVAgree, submitting, error, slug, onSubmit, onClose }) => {
  const [sigMode, setSigMode] = useState('draw'); // draw | type
  const set = (k, v) => setVForm(p => ({ ...p, [k]: v }));
  const input = 'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500';
  const canSubmit = vAgree && (sigMode === 'draw' ? !!signature : !!typedName.trim());

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Vollmacht digital erteilen</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
        </div>

        {/* Vollmachtsart */}
        <p className="text-sm font-medium text-gray-700 mb-2">Umfang der Vollmacht</p>
        <div className="space-y-2 mb-4">
          {[
            { v: 'komplett', t: 'Kompletter Wechselservice', d: 'Daten einholen, alten Vertrag kuendigen, neuen abschliessen, SEPA — Rundum-Service.' },
            { v: 'daten', t: 'Nur Datenerhebung', d: 'Wir holen lediglich Ihre Verbrauchsdaten & Vertragsunterlagen ein.' },
          ].map(o => (
            <label key={o.v} className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${vForm.vollmacht_type === o.v ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>
              <input type="radio" checked={vForm.vollmacht_type === o.v} onChange={() => set('vollmacht_type', o.v)} className="mt-1 text-teal-600" />
              <div><span className="font-medium text-sm text-gray-900">{o.t}</span><p className="text-xs text-gray-500">{o.d}</p></div>
            </label>
          ))}
        </div>

        {/* Kundentyp */}
        <div className="flex gap-2 mb-3">
          {[['gewerbe', 'Gewerbe'], ['privat', 'Privat']].map(([v, t]) => (
            <button key={v} type="button" onClick={() => set('kundentyp', v)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${vForm.kundentyp === v ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600'}`}>{t}</button>
          ))}
        </div>

        {/* Felder */}
        <div className="space-y-3 mb-4">
          <input className={input} placeholder="Firma" value={vForm.firma} onChange={e => set('firma', e.target.value)} />
          <input className={input} placeholder="Name / Geschaeftsfuehrer" value={vForm.name} onChange={e => set('name', e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Strasse, Hausnr." value={vForm.strasse} onChange={e => set('strasse', e.target.value)} />
            <input className={input} placeholder="PLZ, Ort" value={vForm.plz_ort} onChange={e => set('plz_ort', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Zaehlernummer (optional)" value={vForm.zaehlernummer} onChange={e => set('zaehlernummer', e.target.value)} />
            <input className={input} placeholder="Ort (Unterschrift)" value={vForm.ort} onChange={e => set('ort', e.target.value)} />
          </div>
        </div>

        {/* Unterschrift */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Unterschrift</p>
            <div className="flex gap-1 text-xs">
              <button type="button" onClick={() => setSigMode('draw')} className={`px-2 py-1 rounded ${sigMode === 'draw' ? 'bg-teal-100 text-teal-700' : 'text-gray-500'}`}>Zeichnen</button>
              <button type="button" onClick={() => setSigMode('type')} className={`px-2 py-1 rounded ${sigMode === 'type' ? 'bg-teal-100 text-teal-700' : 'text-gray-500'}`}>Tippen</button>
            </div>
          </div>
          {sigMode === 'draw'
            ? <SignaturePad onChange={setSignature} />
            : <input className={`${input} text-lg`} style={{ fontFamily: 'cursive' }} placeholder="Ihr Name als Unterschrift" value={typedName} onChange={e => setTypedName(e.target.value)} />
          }
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mb-4">
          <input type="checkbox" checked={vAgree} onChange={e => setVAgree(e.target.checked)} className="mt-1 text-teal-600" />
          <span>Ich erteile der AIQCON GmbH die oben gewaehlte Vollmacht und bestaetige die Datenschutzhinweise (Art. 13 DSGVO).</span>
        </label>

        {error && (
          <div role="alert" className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <FiAlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-amber-900">
              <p className="font-medium">Ihre Vollmacht wurde noch nicht uebermittelt.</p>
              <p className="mt-1">{error}</p>
              <p className="mt-1 text-xs text-amber-800">
                Ihre Angaben und Ihre Unterschrift bleiben erhalten. Alternativ koennen Sie die{' '}
                <a href={`/api/shared-kalkulationen/${slug}/vollmacht-pdf`} target="_blank" rel="noreferrer" className="underline font-medium">
                  Vollmacht als PDF herunterladen
                </a>{' '}und uns unterschrieben zusenden.
              </p>
            </div>
          </div>
        )}

        <button onClick={onSubmit} disabled={!canSubmit || submitting}
          className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg font-semibold flex items-center justify-center gap-2">
          {submitting ? <FiRefreshCw className="animate-spin" size={18} /> : <FiCheck size={18} />}
          {submitting ? 'Wird uebermittelt...' : error ? 'Erneut senden' : 'Vollmacht rechtsverbindlich erteilen'}
        </button>
      </motion.div>
    </div>
  );
};

// ============================================================
// Hauptkomponente
// ============================================================
const KalkulationView = () => {
  const { slug } = useParams();
  const [kalkulation, setKalkulation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [selectedLaufzeit, setSelectedLaufzeit] = useState(null);
  const [futures, setFutures] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agree, setAgree] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  // Vollmacht
  const [showVollmacht, setShowVollmacht] = useState(false);
  const [vollmachtDone, setVollmachtDone] = useState(false);
  const [vForm, setVForm] = useState({
    vollmacht_type: 'komplett', kundentyp: 'gewerbe', firma: '', name: '',
    geburtsdatum: '', strasse: '', plz_ort: '', zaehlernummer: '', ort: '',
  });
  const [signature, setSignature] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [vAgree, setVAgree] = useState(false);
  const [vSubmitting, setVSubmitting] = useState(false);
  const [vollmachtError, setVollmachtError] = useState(null);

  // Angebot laden — ausschliesslich vom Server. Kein Fallback auf einen alten
  // Browser-Schnappschuss: ein Ladefehler darf nie wie ein gueltiges Angebot aussehen.
  const ladeKalkulation = useCallback(async () => {
    setReloading(true);
    setLoadError(false);
    setNotFound(false);
    try {
      // ?preview=1 aus der Backoffice-Vorschau weiterreichen: der Server zaehlt
      // den Aufruf dann nicht als Kundenoeffnung und laesst den Lead in Ruhe.
      const vorschau = new URLSearchParams(window.location.search).get('preview') === '1';
      const resp = await apiFetch(`/api/shared-kalkulationen/${slug}${vorschau ? '?preview=1' : ''}`);
      if (resp.status === 404) { setNotFound(true); return; }
      if (!resp.ok) { setLoadError(true); return; }
      setKalkulation(await resp.json());
    } catch {
      setLoadError(true);
    } finally {
      setReloading(false);
    }
  }, [slug]);

  useEffect(() => { ladeKalkulation(); }, [ladeKalkulation]);

  // Vollmacht-Formular aus Kalkulationsdaten vorbefuellen
  useEffect(() => {
    if (kalkulation) {
      setVForm(prev => ({
        ...prev,
        firma: prev.firma || kalkulation.company_name || '',
        name: prev.name || kalkulation.contact_name || '',
        // Anschrift der Verbrauchsstelle aus dem Lead. Bisher stand hier nur die
        // PLZ — der Kunde musste Strasse und Ort abtippen, obwohl beides bekannt ist.
        strasse: prev.strasse || kalkulation.strasse || '',
        // kunde_plz zuerst: kalkulation.plz bestimmt nur das Netzgebiet und kann
        // im Rechner abweichend gesetzt worden sein. Auf einer Vollmacht muss die
        // Anschrift der Verbrauchsstelle stehen, nicht die Rechenannahme.
        plz_ort: prev.plz_ort || [kalkulation.kunde_plz || kalkulation.plz, kalkulation.ort].filter(Boolean).join(' '),
        // Ort der Unterschrift: der Firmensitz ist die naheliegende Vorbelegung.
        ort: prev.ort || kalkulation.ort || '',
      }));
      setTypedName(prev => prev || kalkulation.contact_name || '');
    }
  }, [kalkulation]);

  const handleVollmacht = async () => {
    if (!vAgree || (!signature && !typedName.trim())) return;
    setVSubmitting(true);
    setVollmachtError(null);
    const payload = {
      ...vForm,
      signature: signature || null,
      typed_name: signature ? null : typedName,
      email: kalkulation?.contact_email || '',
      datum: new Date().toISOString(),
      signed_at: new Date().toISOString(),
    };
    try {
      const resp = await apiFetch(`/api/shared-kalkulationen/${slug}/vollmacht`, {
        method: 'POST', body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        // Dialog bleibt offen, Unterschrift und Eingaben bleiben erhalten.
        const hinweis = await serverHinweis(resp);
        setVollmachtError(hinweis || 'Die Uebermittlung ist leider fehlgeschlagen. Bitte senden Sie in einem Moment noch einmal.');
        return;
      }
    } catch {
      setVollmachtError(NETZ_HINWEIS);
      return;
    } finally {
      setVSubmitting(false);
    }
    // Erfolg ist bestaetigt — erst jetzt darf der Kunde "erteilt" sehen.
    setShowVollmacht(false);
    setVollmachtDone(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    apiFetch('/api/futures')
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data && data.prices) setFutures(data); })
      .catch(() => {});
  }, []);

  // Optionen ableiten: pro Laufzeit das guenstigste Angebot. Verglichen wird mit
  // dem Preis, der dem Kunden auch angezeigt wird (neu: Energiepreis, alt:
  // Komplettpreis). Angebote ohne Preis fallen raus — zuvor gewann ein fehlender
  // Preis den Vergleich, weil er als 0 gezaehlt wurde.
  const buildOptions = (preisVon) => {
    const { results = [], laufzeiten = [] } = kalkulation || {};
    return laufzeiten.map(lz => {
      let best = null;
      let bestPreis = null;
      results.forEach(r => {
        const m = r.laufzeiten?.find(l => l.laufzeit_monate === lz);
        const p = preisVon(m);
        if (m && p != null && (bestPreis == null || p < bestPreis)) {
          best = { ...m, supplier_name: r.supplier_name };
          bestPreis = p;
        }
      });
      return best;
    }).filter(Boolean);
  };

  const handleConfirm = async () => {
    if (!agree) return;
    setSubmitting(true);
    setConfirmError(null);
    const opt = options.find(o => o.laufzeit_monate === effectiveLaufzeit);
    const payload = {
      beauftragt: true,
      preferred_laufzeit_monate: effectiveLaufzeit,
      // Preisbasis mitschicken: im Backoffice muss eindeutig sein, worauf sich
      // die bestaetigte Spanne bezieht — Energiepreis oder alter Komplettpreis.
      preisbasis: energiebasis ? 'energie' : 'komplett',
      preferred_angebotspreis_ct_kwh: energiebasis ? preisVon(opt) : null,
      preferred_komplettpreis_ct_kwh: opt?.komplettpreis_ct_kwh ?? null,
      preis_min_ct_kwh: priceRange.min,
      preis_max_ct_kwh: priceRange.max,
      note,
      bestaetigt_am: new Date().toISOString(),
    };
    try {
      const resp = await apiFetch(`/api/shared-kalkulationen/${slug}/accept`, {
        method: 'POST', body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        // Dialog bleibt offen, Auswahl und Anmerkung bleiben erhalten.
        const hinweis = await serverHinweis(resp);
        setConfirmError(hinweis || 'Ihre Beauftragung konnte gerade nicht gespeichert werden. Bitte versuchen Sie es in einem Moment noch einmal.');
        return;
      }
    } catch {
      setConfirmError(NETZ_HINWEIS);
      return;
    } finally {
      setSubmitting(false);
    }
    // Erst nach bestaetigter Serverantwort gilt die Beauftragung als erfolgt.
    setShowConfirm(false);
    setAccepted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Render-Zustaende ---
  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <FiAlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-900">Angebot nicht gefunden</h2>
          <p className="text-gray-500 mt-2">Dieser Link ist ungueltig oder das Angebot ist nicht mehr verfuegbar.</p>
        </div>
      </div>
    );
  }
  // Ladefehler — bewusst getrennt von "nicht gefunden": das Angebot existiert,
  // wir kommen nur gerade nicht daran.
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <FiAlertCircle className="mx-auto text-teal-600 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-900">Ihr Angebot ist gerade nicht abrufbar</h2>
          <p className="text-gray-500 mt-2">
            Die Verbindung zu unserem Server ist momentan gestoert. Ihr Angebot ist weiterhin hinterlegt —
            bitte versuchen Sie es in einem Moment noch einmal.
          </p>
          <button onClick={ladeKalkulation} disabled={reloading}
            className="mt-5 inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-lg">
            <FiRefreshCw className={reloading ? 'animate-spin' : ''} size={18} />
            {reloading ? 'Wird geladen...' : 'Erneut versuchen'}
          </button>
          <p className="text-xs text-gray-400 mt-6 flex items-center justify-center gap-1">
            <FiPhone size={12} /> Bleibt es dabei, melden Sie sich gerne bei Ihrem persoenlichen Ansprechpartner.
          </p>
        </div>
      </div>
    );
  }
  if (!kalkulation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  const { verbrauch, netzbetreiber, plz, netzentgelt_ct_kwh, abgaben_ct_kwh, current_price_ct_kwh } = kalkulation;
  // Ist das Netzentgelt ein regionaler Richtwert statt eines Preisblatts?
  // Aeltere Angebote tragen die Angabe nicht — dann bleiben wir beim
  // vorsichtigeren Wortlaut, statt eine Genauigkeit zu behaupten.
  const netzGeschaetzt = kalkulation.netzentgelt_herkunft !== 'preisblatt';

  // RLM-Angebote nennen den reinen Energiepreis — branchenueblich wird bei
  // registrierender Leistungsmessung kein Gesamtpreis quotiert, weil die
  // Netznutzung ueber Leistungs- und Arbeitspreis separat abgerechnet wird.
  // Fehlt die Angabe (Altangebote vor der Umstellung), bleibt es beim
  // bisherigen SLP-Verhalten mit vollstaendiger Aufschluesselung.
  const istRLM = String(kalkulation.zaehlverfahren || '').trim().toUpperCase() === 'RLM';

  // Alt oder neu? Siehe istEnergiebasis(): entschieden wird nach den Daten des
  // Angebots, damit ein bereits versendeter Link unveraendert bleibt.
  const energiebasis = istEnergiebasis(kalkulation);
  const preisVon = (lz) => (energiebasis ? energiepreisVon(lz) : zahl(lz?.komplettpreis_ct_kwh));
  const jahrVon = (lz) => {
    if (!energiebasis) return zahl(lz?.komplettpreis_jahr_eur);
    if (lz?.angebotspreis_jahr_eur != null) return zahl(lz.angebotspreis_jahr_eur);
    const ct = energiepreisVon(lz);
    const menge = zahl(verbrauch);
    return (ct == null || !menge) ? null : (ct / 100) * menge;
  };

  const options = buildOptions(preisVon);
  const effectiveLaufzeit = selectedLaufzeit || (options[0]?.laufzeit_monate ?? null);
  const prices = options.map(o => preisVon(o)).filter(v => v != null);
  // Ohne Preise gibt es keine Spanne: null statt Infinity, damit '–' erscheint
  // und keine erfundene Zahl.
  const priceRange = prices.length
    ? { min: Math.min(...prices), max: Math.max(...prices) }
    : { min: null, max: null };
  const spanneKurz = priceRange.min == null ? '–'
    : (priceRange.min === priceRange.max ? ctText(priceRange.min) : `${ctText(priceRange.min)}–${ctText(priceRange.max)}`);
  const spanneWeit = priceRange.min == null ? '–'
    : (priceRange.min === priceRange.max ? ctText(priceRange.min) : `${ctText(priceRange.min)} – ${ctText(priceRange.max)}`);
  const bestOption = options.reduce((b, o) => {
    const p = preisVon(o);
    const bp = preisVon(b);
    return (b == null || (p != null && (bp == null || p < bp))) ? o : b;
  }, null);

  // Regulierter, nicht verhandelbarer Teil — wird ausgewiesen, aber nicht in den
  // Angebotspreis gerechnet. Fehlt ein Wert, bleibt er '–' (keine 0).
  const netzWert = zahl(netzentgelt_ct_kwh);
  const abgabenWert = zahl(abgaben_ct_kwh);
  const regulierteSumme = (netzWert != null && abgabenWert != null) ? netzWert + abgabenWert : null;
  const abgabenZeilen = Object.entries(kalkulation.abgaben_detail || {})
    .filter(([k, v]) => ABGABEN_LABEL[k] && zahl(v) != null)
    .map(([k, v]) => [ABGABEN_LABEL[k], zahl(v)]);

  const daysLeft = kalkulation.valid_until
    ? Math.max(0, Math.ceil((new Date(kalkulation.valid_until) - new Date()) / 86400000)) : null;

  // Bestaetigungs-Screen
  if (accepted) {
    const opt = options.find(o => o.laufzeit_monate === effectiveLaufzeit) || bestOption;
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiCheck className="text-green-600" size={40} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Vielen Dank{kalkulation.contact_name ? `, ${kalkulation.contact_name}` : ''}!</h1>
              <p className="text-gray-600 mt-3 text-lg">
                Sie haben uns beauftragt, fuer Sie taetig zu werden. Wir holen jetzt die verbindlichen Angebote ein.
              </p>
            </div>
            {/* Vollmacht-Schritt */}
            {!vollmachtDone ? (
              <Card className="border-2 border-teal-200">
                <div className="text-center">
                  <FiEdit3 className="mx-auto text-teal-600 mb-2" size={28} />
                  <h3 className="font-bold text-gray-900">Letzter Schritt: Vollmacht erteilen</h3>
                  <p className="text-sm text-gray-600 mt-1 mb-4">
                    Damit wir verbindliche Angebote einholen und den Wechsel fuer Sie abwickeln koennen, benoetigen wir Ihre Vollmacht. Digital in 1 Minute erteilt.
                  </p>
                  <button onClick={() => { setVollmachtError(null); setShowVollmacht(true); }}
                    className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-lg">
                    <FiEdit3 size={18} /> Vollmacht jetzt digital erteilen
                  </button>
                  <div className="mt-3">
                    <a href={`/api/shared-kalkulationen/${slug}/vollmacht-pdf`} target="_blank" rel="noreferrer"
                      className="text-sm text-teal-700 hover:underline inline-flex items-center gap-1">
                      <FiDownload size={14} /> Stattdessen Vollmacht als PDF herunterladen
                    </a>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="bg-green-50 border-green-200">
                <div className="text-center">
                  <FiCheck className="mx-auto text-green-600 mb-2" size={28} />
                  <h3 className="font-bold text-gray-900">Vollmacht erteilt — vielen Dank!</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Wir haben Ihre Vollmacht erhalten und werden umgehend fuer Sie taetig. Eine Kopie geht Ihnen per E-Mail zu.
                  </p>
                  <a href={`/api/shared-kalkulationen/${slug}/vollmacht-pdf`} target="_blank" rel="noreferrer"
                    className="text-sm text-teal-700 hover:underline inline-flex items-center gap-1 mt-3">
                    <FiDownload size={14} /> Vollmacht als PDF ansehen
                  </a>
                </div>
              </Card>
            )}

            <Card>
              <h3 className="font-semibold text-gray-900 mb-3">So geht es weiter</h3>
              <div className="space-y-4">
                {['Wir holen fuer Ihre Verbrauchsstelle verbindliche Versorger-Angebote ein.',
                  'Ihr persoenlicher Ansprechpartner meldet sich in Kuerze bei Ihnen.',
                  'Wir wickeln den Wechsel ab und ueberwachen Ihren Vertrag laufend.'
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal-700 text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="text-sm text-gray-700">{t}</p>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <p className="text-sm text-gray-600 mb-3 text-center">Ihre bestaetigte Auswahl</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Bevorzugte Laufzeit</p>
                  <p className="text-lg font-bold">{Math.round((opt?.laufzeit_monate || 0) / 12)} Jahre</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">{energiebasis ? 'Preisspanne Energiepreis' : 'Preisspanne'}</p>
                  <p className="text-lg font-bold">{spanneKurz} ct/kWh</p>
                  {energiebasis && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{ZUSATZ}</p>}
                </div>
              </div>
            </Card>
            <p className="text-center text-xs text-gray-400">&copy; {new Date().getFullYear()} AIQCON GmbH</p>
          </motion.div>
        </div>

        {/* Vollmacht-Dialog */}
        {showVollmacht && (
          <VollmachtDialog
            vForm={vForm} setVForm={setVForm}
            signature={signature} setSignature={setSignature}
            typedName={typedName} setTypedName={setTypedName}
            vAgree={vAgree} setVAgree={setVAgree}
            submitting={vSubmitting} error={vollmachtError} slug={slug}
            onSubmit={handleVollmacht}
            onClose={() => { setShowVollmacht(false); setVollmachtError(null); }}
          />
        )}
      </div>
    );
  }

  // EEX-Futures Chart-Daten
  const futureYears = futures?.prices
    ? Object.keys(futures.prices).filter(y => +y >= new Date().getFullYear()).sort().slice(0, 6)
    : [];
  const futuresChart = futureYears.length ? {
    grid: { left: 40, right: 20, top: 20, bottom: 30 },
    tooltip: { trigger: 'axis', valueFormatter: v => v.toFixed(2) + ' ct/kWh' },
    xAxis: { type: 'category', data: futureYears.map(y => 'Cal-' + y), axisLabel: { color: '#6b7280' } },
    yAxis: { type: 'value', axisLabel: { color: '#6b7280', formatter: '{value} ct' }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
    series: [{
      type: 'bar', data: futureYears.map(y => futures.prices[y]),
      itemStyle: { color: TEAL, borderRadius: [4, 4, 0, 0] }, barWidth: '50%',
      label: { show: true, position: 'top', formatter: p => p.value.toFixed(2), color: '#374151', fontSize: 10 },
    }],
  } : null;

  // Ersparnis — Energie gegen Energie.
  // current_price_ct_kwh ist der aktuelle ARBEITSPREIS des Kunden (Feld
  // "Aktueller Preis (ct/kWh)" am Unternehmen). Die passende Vergleichsgroesse
  // dazu ist unser Energiepreis: beide enthalten Beschaffung und die Marge des
  // Lieferanten, keiner von beiden enthaelt Netzentgelte, Abgaben oder Umlagen.
  const aktuellerArbeitspreis = zahl(current_price_ct_kwh);
  const offerEnergie = energiebasis ? preisVon(bestOption) : zahl(bestOption?.energie_ct_kwh);
  // Plausibilitaetsschranke: liegt der genannte "aktuelle Preis" ueber unserem
  // Gesamtpreis inklusive Netzentgelten und Abgaben, ist es offenkundig kein
  // reiner Arbeitspreis, sondern ein Gesamtpreis. Der Vergleich waere dann
  // schoengerechnet — dann lieber weglassen als eine zu hohe Ersparnis zeigen.
  const gesamtRichtwert = (offerEnergie != null && regulierteSumme != null)
    ? offerEnergie + regulierteSumme : null;
  const vergleichUnsicher = energiebasis && gesamtRichtwert != null
    && aktuellerArbeitspreis != null && aktuellerArbeitspreis >= gesamtRichtwert;
  const hasSavings = aktuellerArbeitspreis != null && offerEnergie != null
    && aktuellerArbeitspreis > offerEnergie && !vergleichUnsicher;
  const annualSaving = hasSavings ? ((aktuellerArbeitspreis - offerEnergie) / 100) * (zahl(verbrauch) || 0) : 0;
  const savingsChart = hasSavings ? {
    grid: { left: 50, right: 20, top: 10, bottom: 40 },
    tooltip: { trigger: 'axis', valueFormatter: v => v.toFixed(2) + ' ct/kWh' },
    xAxis: {
      type: 'category',
      data: energiebasis ? ['Heute (Arbeitspreis)', 'AIQCON (Energiepreis)'] : ['Aktuell', 'AIQCON'],
      axisLabel: { color: '#6b7280', interval: 0, fontSize: 11 },
    },
    yAxis: { type: 'value', axisLabel: { color: '#6b7280', formatter: '{value} ct' }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
    series: [{
      type: 'bar', barWidth: '45%',
      data: [
        { value: aktuellerArbeitspreis, itemStyle: { color: '#9ca3af', borderRadius: [4, 4, 0, 0] } },
        { value: offerEnergie, itemStyle: { color: TEAL, borderRadius: [4, 4, 0, 0] } },
      ],
      label: { show: true, position: 'top', formatter: p => p.value.toFixed(2), color: '#374151' },
    }],
  } : null;

  // Donut (Fallback, wenn kein Vergleich moeglich ist). Im neuen Modus ist der
  // Energiepreis der Angebotsbestandteil, Netz und Abgaben sind es nicht — und
  // gezeichnet wird nur, wenn wirklich alle drei Werte vorliegen.
  const donutTeile = energiebasis
    ? [
      { value: preisVon(bestOption), name: 'Energiepreis (unser Angebot)', itemStyle: { color: TEAL } },
      { value: netzWert, name: 'Netzentgelte (nicht im Angebot)', itemStyle: { color: '#5eead4' } },
      { value: abgabenWert, name: 'Abgaben & Umlagen (nicht im Angebot)', itemStyle: { color: '#99f6e4' } },
    ]
    : [
      { value: bestOption?.energie_ct_kwh, name: 'Energie', itemStyle: { color: TEAL } },
      { value: netzentgelt_ct_kwh, name: 'Netzentgelte', itemStyle: { color: '#5eead4' } },
      { value: abgaben_ct_kwh, name: 'Abgaben & Umlagen', itemStyle: { color: '#99f6e4' } },
    ];
  // Bei RLM bewusst kein Donut: er stellt Energiepreis, Netzentgelt und Abgaben
  // als Anteile eines Ganzen dar — genau die Gesamtpreis-Sicht, die ein
  // RLM-Angebot nicht behauptet.
  const donut = (!istRLM && bestOption && (!energiebasis || donutTeile.every(t => t.value != null))) ? {
    tooltip: { trigger: 'item', valueFormatter: v => v.toFixed(2) + ' ct/kWh' },
    legend: { bottom: 0, textStyle: { color: '#6b7280' } },
    series: [{
      type: 'pie', radius: ['45%', '70%'], center: ['50%', '42%'], avoidLabelOverlap: false,
      label: { show: false }, data: donutTeile,
    }],
  } : null;

  const Section = ({ icon: Icon, title, subtitle, children }) => (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="text-teal-600" size={20} />
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
      {subtitle && <p className="text-sm text-gray-500 mb-4">{subtitle}</p>}
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FiZap size={24} />
                <span className="text-sm font-medium opacity-80">AIQCON Energy &middot; Ihr unabhaengiger Energiemakler</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold">
                {kalkulation.contact_name ? `Ihr Stromangebot, ${kalkulation.contact_name}` : 'Ihr persoenliches Stromangebot'}
              </h1>
              {kalkulation.company_name && <p className="text-teal-100 mt-1 text-lg">{kalkulation.company_name}</p>}
              {kalkulation.personal_message && <p className="text-teal-100 mt-3 max-w-2xl opacity-90">{kalkulation.personal_message}</p>}
            </div>
            <a href={`/api/shared-kalkulationen/${slug}/pdf`} target="_blank" rel="noreferrer"
              className="flex-shrink-0 flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur px-4 py-2 rounded-lg text-sm font-medium transition-all">
              <FiFileText size={16} /> PDF
            </a>
          </div>
          {/* Preisspanne-Banner */}
          {prices.length > 0 && (
            <div className="mt-6 inline-flex items-start gap-3 bg-white/10 backdrop-blur rounded-xl px-5 py-3">
              <FiBarChart2 size={22} className="mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs opacity-80">
                  {energiebasis ? 'Ihre Preisspanne (Energiepreis netto)' : 'Ihre Preisspanne (Komplettpreis netto)'}
                </p>
                <p className="text-2xl font-bold">{spanneWeit} <span className="text-base font-normal">ct/kWh</span></p>
                {energiebasis && <p className="text-xs opacity-90 mt-0.5">{ZUSATZ}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Meta-Info */}
        <div className="flex flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-white rounded-lg px-4 py-2 border">
            <FiZap className="text-teal-600" size={16} /><span>{verbrauch?.toLocaleString('de-DE')} kWh/a</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-white rounded-lg px-4 py-2 border">
            <FiCalendar className="text-teal-600" size={16} />
            <span>
              PLZ {plz}
              {netzbetreiber && netzbetreiber !== 'Unbekannt' ? ` · Netzgebiet ${netzbetreiber}` : ''}
            </span>
          </div>
          {daysLeft != null && (
            <div className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2 border ${daysLeft <= 3 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-gray-600'}`}>
              <FiClock size={16} /><span>{daysLeft > 0 ? `Noch ${daysLeft} Tage gueltig` : 'Laeuft heute ab!'}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-white rounded-lg px-4 py-2 border">
            <FiTrendingDown className="text-green-600" size={16} />
            <span>Boersenbasiert (EEX){kalkulation.stand ? `, Stand ${new Date(kalkulation.stand).toLocaleDateString('de-DE')}` : ''}</span>
          </div>
        </div>

        {/* Preisoptionen */}
        <Section icon={FiZap} title="Ihre Preisoptionen" subtitle="Waehlen Sie Ihre bevorzugte Laufzeit. Je laenger die Bindung, desto planbarer Ihr Preis.">
          {/* Angebot geladen, aber ohne Preisangabe: das ist etwas anderes als ein
              Ladefehler und muss auch anders aussehen — keine leere Flaeche, keine 0. */}
          {options.length === 0 && (
            <div role="status" className="bg-white rounded-xl border p-6">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="text-teal-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Zu diesem Angebot liegt uns derzeit keine Preisangabe vor.</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Ihr Angebot konnte geladen werden, es enthaelt an dieser Stelle aber keinen Preis — wir zeigen Ihnen
                    lieber gar keine Zahl als eine falsche. Bitte melden Sie sich kurz bei Ihrem persoenlichen
                    Ansprechpartner, dann klaeren wir das umgehend.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {options.map(o => (
              <OptionCard
                key={o.laufzeit_monate}
                option={o}
                preis={preisVon(o)}
                jahrEur={jahrVon(o)}
                energiebasis={energiebasis}
                isSelected={effectiveLaufzeit === o.laufzeit_monate}
                isBest={bestOption && o.laufzeit_monate === bestOption.laufzeit_monate}
                onSelect={setSelectedLaufzeit}
              />
            ))}
          </div>
          {/* Detail der gewaehlten Option */}
          {(() => {
            const o = options.find(x => x.laufzeit_monate === effectiveLaufzeit);
            if (!o) return null;
            // Neue Darstellung: Angebotsbestandteil und regulierter Rest klar getrennt.
            if (energiebasis) {
              return (
                <div className="mt-4 bg-white rounded-xl border p-5">
                  <p className="text-sm font-medium text-gray-700 mb-3">Ihr Angebot fuer die gewaehlte Laufzeit</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="rounded-lg bg-teal-50 border border-teal-200 p-4">
                      <p className="text-xs font-semibold text-teal-800 uppercase tracking-wide">Im Angebot enthalten</p>
                      <p className="text-gray-500 text-xs mt-2">Energiepreis — der Teil, den wir fuer Sie verhandeln</p>
                      <p className="text-2xl font-bold text-teal-700">
                        {ctText(preisVon(o), 3)} <span className="text-sm font-normal">ct/kWh</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">{ZUSATZ}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Energiekosten pro Jahr: <strong>{fmtEUR2(jahrVon(o))}</strong> bei{' '}
                        {verbrauch?.toLocaleString('de-DE') || '–'} kWh/a, netto.
                      </p>
                    </div>
                    <div className="rounded-lg bg-gray-50 border p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Nicht im Angebot enthalten</p>
                      {istRLM ? (
                        <>
                          <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                            Netznutzung, Abgaben und Umlagen
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Diese Positionen rechnet Ihr Netzbetreiber ueber Leistungs- und Arbeitspreis separat ab. Sie
                            sind reguliert, fallen bei jedem Versorger in identischer Hoehe an und werden unveraendert
                            weitergegeben — deshalb nennen wir hier keinen Schaetzwert.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="mt-2 space-y-1">
                            <div className="flex justify-between gap-3">
                              <span className="text-gray-600">Netzentgelte{netzGeschaetzt ? ' (Richtwert)' : ''}</span>
                              <span className="font-semibold text-gray-900 whitespace-nowrap">
                                {ctText(netzWert, netzGeschaetzt ? 2 : 3)} ct/kWh
                              </span>
                            </div>
                            <div className="flex justify-between gap-3">
                              <span className="text-gray-600">Abgaben und Umlagen</span>
                              <span className="font-semibold text-gray-900 whitespace-nowrap">{ctText(abgabenWert, 3)} ct/kWh</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Diese Positionen zahlt jeder Stromkunde in gleicher Hoehe — unabhaengig davon, welchen Versorger
                            er waehlt. Was dahintersteckt, steht im naechsten Abschnitt.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="mt-4 bg-white rounded-xl border p-5">
                <p className="text-sm font-medium text-gray-700 mb-3">Preiszusammensetzung (gewaehlte Laufzeit)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {[['Energiepreis', o.energie_ct_kwh, 3], ['Netzentgelte', netzentgelt_ct_kwh, netzGeschaetzt ? 2 : 3], ['Abgaben & Umlagen', abgaben_ct_kwh, 3], ['Komplettpreis', o.komplettpreis_ct_kwh, netzGeschaetzt ? 2 : 3]].map(([k, v, nk], i) => (
                    <div key={k} className={i === 3 ? 'border-l pl-4' : ''}>
                      <p className="text-gray-400 text-xs">{k}</p>
                      <p className={`font-bold ${i === 3 ? 'text-teal-600' : 'text-gray-900'}`}>{v?.toFixed(nk)} ct/kWh</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  Jahreskosten netto: <strong>{fmtEUR2(o.komplettpreis_jahr_eur)}</strong> bei {verbrauch?.toLocaleString('de-DE')} kWh/a.
                  {netzGeschaetzt ? (
                    <>
                      {' '}Netzentgelte kalkuliert mit dem fuer Ihr Netzgebiet vorliegenden Richtwert; Abgaben und Umlagen nach den
                      gesetzlichen Saetzen. Beide werden vom Netzbetreiber bzw. Gesetzgeber festgelegt und jaehrlich angepasst —
                      der genaue Netzentgeltsatz wird vor Vertragsabschluss anhand des aktuellen Preisblatts bestaetigt.
                    </>
                  ) : (
                    <>
                      {' '}Netzentgelte gemaess dem Preisblatt Ihres oertlichen Netzbetreibers, Abgaben und Umlagen nach den gesetzlichen
                      Saetzen — beide werden von Netzbetreiber bzw. Gesetzgeber festgelegt, jaehrlich angepasst und von uns
                      unveraendert weitergegeben.
                    </>
                  )}
                </p>
              </div>
            );
          })()}
        </Section>

        {/* Was nicht im Angebot steckt — bewusst VOR der Beauftragung, nicht im Kleingedruckten.
            Bei RLM steht hier bewusst KEINE Netzentgelt-Tabelle und KEIN hochgerechneter
            Gesamtpreis: quotiert wird der reine Energiepreis. Ein regionaler Richtwert
            waere an dieser Stelle eine Zahl, zu der wir nicht stehen koennen. */}
        {energiebasis && (istRLM ? (
          <Section icon={FiShield} title="Was dieses Angebot umfasst"
            subtitle="Ein reiner Energiepreis — bei registrierender Leistungsmessung die uebliche Angebotsform">
            <Card>
              <p className="text-sm text-gray-700 leading-relaxed">
                Dieses Angebot nennt Ihren <strong>Energiepreis</strong> — den Strom selbst, beschafft an der Boerse.
                Das ist der Teil, den wir fuer Sie verhandeln, und der einzige Teil, in dem sich Versorger ueberhaupt
                unterscheiden.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-3">
                Nicht enthalten sind <strong>Netznutzung, Abgaben und Umlagen</strong>: die Netzentgelte und der
                Messstellenbetrieb Ihres oertlichen Netzbetreibers sowie Stromsteuer, KWKG-Umlage, Offshore-Netzumlage,
                §19-StromNEV-Umlage und Konzessionsabgabe. Diese Positionen sind reguliert, werden jaehrlich neu
                festgesetzt und fallen bei jedem Versorger in identischer Hoehe an. Sie werden Ihnen unveraendert
                weiterberechnet.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-3">
                Bei registrierender Leistungsmessung ist das die uebliche Angebotsform: verglichen wird der
                Energiepreis, nicht ein Gesamtpreis. Die fuer Ihre Entnahmestelle gueltigen Netzentgelte weist das
                Preisblatt Ihres Netzbetreibers aus{netzbetreiber && netzbetreiber !== 'Unbekannt' ? ` (${netzbetreiber})` : ''}.
              </p>
            </Card>
          </Section>
        ) : (
          <Section icon={FiShield} title="Netzentgelte, Abgaben und Umlagen"
            subtitle="Der Teil Ihres Strompreises, den kein Versorger beeinflussen kann">
            <Card>
              <p className="text-sm text-gray-700 leading-relaxed">
                Ihr Strompreis besteht aus zwei Teilen. Der eine ist der <strong>Energiepreis</strong> — der Strom selbst,
                beschafft an der Boerse. Das ist der Teil, den wir fuer Sie verhandeln, und genau dieser Teil steht in
                diesem Angebot.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-3">
                Der andere Teil sind <strong>Netzentgelte, Abgaben und Umlagen</strong>. Netzentgelte sind die Kosten fuer
                den Transport des Stroms durch das Netz Ihres oertlichen Netzbetreibers. Stromsteuer, KWKG-Umlage,
                Offshore-Netzumlage, §19-StromNEV-Umlage und Konzessionsabgabe sind gesetzlich festgelegt. Beides faellt in
                identischer Hoehe an, gleichgueltig fuer welchen Versorger Sie sich entscheiden; beides wird jaehrlich neu
                festgesetzt und ist nicht verhandelbar. Deshalb ist es nicht Bestandteil unseres Angebots. Fuer Sie hat das
                einen praktischen Vorteil: Sie sehen genau die Zahl, um die es beim Wechsel wirklich geht, und koennen
                Angebote unmittelbar miteinander vergleichen.
              </p>

              <div className="mt-5 rounded-lg border divide-y overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Netzentgelte</p>
                    <p className="text-xs text-gray-500">
                      {netzbetreiber && netzbetreiber !== 'Unbekannt' ? `Netzgebiet ${netzbetreiber}` : `PLZ ${plz}`}
                      {netzGeschaetzt
                        ? ' · regionaler Richtwert; den genauen Satz bestaetigt das Preisblatt Ihres Netzbetreibers'
                        : ' · laut Preisblatt Ihres Netzbetreibers'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {ctText(netzWert, netzGeschaetzt ? 2 : 3)} ct/kWh
                  </span>
                </div>
                {abgabenZeilen.length > 0 ? abgabenZeilen.map(([label, wert]) => (
                  <div key={label} className="flex items-start justify-between gap-3 px-4 py-2">
                    <p className="text-sm text-gray-600">{label}</p>
                    <span className="text-sm text-gray-700 whitespace-nowrap">{ctText(wert, 3)} ct/kWh</span>
                  </div>
                )) : (
                  <div className="flex items-start justify-between gap-3 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Abgaben und Umlagen</p>
                      <p className="text-xs text-gray-500">Stromsteuer, KWKG-, Offshore- und §19-Umlage, Konzessionsabgabe</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{ctText(abgabenWert, 3)} ct/kWh</span>
                  </div>
                )}
                <div className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50">
                  <p className="text-sm font-medium text-gray-900">Summe, die zum Energiepreis hinzukommt</p>
                  <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{ctText(regulierteSumme)} ct/kWh</span>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                {(regulierteSumme != null && preisVon(bestOption) != null) ? (
                  <>
                    Zur Orientierung: Zusammen mit dem guenstigsten Energiepreis aus diesem Angebot
                    ({ctText(preisVon(bestOption))} ct/kWh) laege Ihr Gesamtpreis derzeit bei rund{' '}
                    {ctText(preisVon(bestOption) + regulierteSumme)} ct/kWh netto. Dieser Gesamtwert ist keine Zusage
                    von uns: Netzentgelte, Abgaben und Umlagen werden von Netzbetreiber und Gesetzgeber festgesetzt und
                    koennen sich jaehrlich aendern. Verbindlich ist unsererseits allein der Energiepreis.
                  </>
                ) : (
                  <>
                    Fuer Ihr Netzgebiet liegen uns derzeit nicht alle Saetze vollstaendig vor — deshalb steht dort
                    „–“ statt einer Zahl. Die genauen Betraege weist Ihr Netzbetreiber bzw. Ihre Stromrechnung aus.
                    Auf unser Angebot haben sie keinen Einfluss, da sie bei jedem Versorger gleich hoch sind.
                  </>
                )}
              </p>
            </Card>
          </Section>
        ))}

        {/* CTA — ohne Preisspanne gibt es nichts zu bestaetigen */}
        {options.length > 0 && (
        <div className="mt-6 bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900">
            {energiebasis ? 'Mit dieser Preisspanne fuer den Energiepreis einverstanden?' : 'Mit dieser Preisspanne einverstanden?'}
          </h3>
          <p className="text-sm text-gray-600 mt-1 mb-4 max-w-2xl mx-auto">
            {energiebasis
              ? 'Sie bestaetigen damit die Spanne fuer den Energiepreis (zzgl. Netzentgelte, Abgaben und Umlagen) und beauftragen uns, fuer Sie verbindliche Angebote einzuholen. Unverbindlich & kostenfrei.'
              : 'Bestaetigen Sie die Preisspanne und beauftragen Sie uns, fuer Sie verbindliche Angebote einzuholen. Unverbindlich & kostenfrei.'}
          </p>
          <button onClick={() => { setConfirmError(null); setShowConfirm(true); }}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-lg shadow-md transition-all">
            <FiThumbsUp size={18} /> Preisspanne bestaetigen &amp; AIQCON beauftragen
          </button>
        </div>
        )}

        {/* Ersparnis / Aufschluesselung */}
        {hasSavings ? (
          <Section icon={FiTrendingDown} title="Ihr Sparpotenzial"
            subtitle={energiebasis
              ? `Ihr aktueller Arbeitspreis${kalkulation.current_supplier ? ` (${kalkulation.current_supplier})` : ''} im Vergleich zu unserem Energiepreis — beide Werte ohne Netzentgelte, Abgaben und Umlagen`
              : `Vergleich Ihres aktuellen Arbeitspreises${kalkulation.current_supplier ? ` (${kalkulation.current_supplier})` : ''} mit unserem Angebot`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <ReactECharts option={savingsChart} style={{ height: 220 }} />
                {energiebasis && (
                  <p className="text-xs text-gray-400 mt-2">
                    Beide Balken in ct/kWh netto, jeweils reiner Arbeitspreis — ohne Netzentgelte, Abgaben und Umlagen.
                  </p>
                )}
              </Card>
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-xl p-6 flex flex-col justify-center">
                <p className="text-sm opacity-80">
                  {energiebasis ? 'Ihre jaehrliche Ersparnis beim Energiepreis' : 'Ihre jaehrliche Ersparnis (Arbeitspreis)'}
                </p>
                <p className="text-4xl font-bold mt-1">{fmtEUR(annualSaving)}</p>
                <p className="text-sm opacity-80 mt-2">
                  {ctText(aktuellerArbeitspreis)} ct/kWh → {ctText(offerEnergie)} ct/kWh
                </p>
                {energiebasis ? (
                  <p className="text-xs opacity-70 mt-3 leading-relaxed">
                    Unser Wert enthaelt keine Netzentgelte, Abgaben und Umlagen. Wir gehen davon aus, dass die uns
                    genannten {ctText(aktuellerArbeitspreis)} ct/kWh ebenfalls Ihr reiner Arbeitspreis sind und nicht der
                    Gesamtpreis Ihrer Jahresrechnung — nur dann ist der Vergleich aussagekraeftig. Bitte pruefen Sie das
                    kurz und sagen Sie uns Bescheid, falls es abweicht. Netzentgelte und Abgaben sind bei jedem Versorger
                    gleich hoch und aendern sich durch einen Wechsel ohnehin nicht.
                  </p>
                ) : (
                  <p className="text-xs opacity-70 mt-3">Netzentgelte &amp; Abgaben sind unabhaengig vom Versorger und in beiden Faellen gleich.</p>
                )}
              </div>
            </div>
          </Section>
        ) : donut && (
          <Section icon={FiBarChart2}
            title={energiebasis ? 'Woraus sich Ihr Strompreis zusammensetzt' : 'Transparente Preiszusammensetzung'}
            subtitle={energiebasis
              ? 'Nur der Energiepreis ist Gegenstand dieses Angebots — Netzentgelte, Abgaben und Umlagen kommen unveraendert hinzu'
              : 'Ihr Komplettpreis — aufgeschluesselt nach Energie, Netzentgelten und staatlichen Abgaben'}>
            <Card>
              <ReactECharts option={donut} style={{ height: 280 }} />
              {energiebasis && (
                <p className="text-xs text-gray-400 mt-2">
                  Werte in ct/kWh netto{netzGeschaetzt ? '; die Netzentgelte sind ein regionaler Richtwert' : ''}. Verbindlich
                  ist unsererseits allein der Energiepreis.
                </p>
              )}
            </Card>
          </Section>
        )}

        {/* Markt-Insight */}
        {futuresChart && (
          <Section icon={FiActivity} title="Marktlage: Stromboerse (EEX)" subtitle="Ihr Energiepreis orientiert sich an den Boersennotierungen je Lieferjahr — wir geben Ihnen Zugang zu echten Marktpreisen.">
            <Card>
              <ReactECharts option={futuresChart} style={{ height: 260 }} />
              <p className="text-xs text-gray-400 mt-2">
                {futures?.bezeichnung || 'EEX Phelix-DE Baseload Jahres-Futures'}
                {futures?.stand ? `, Settlement ${new Date(futures.stand).toLocaleDateString('de-DE')}` : ''}.{' '}
                {energiebasis
                  ? 'Ihr Energiepreis = Boerse + transparente Strukturkosten; Netzentgelte, Abgaben und Umlagen kommen hinzu.'
                  : 'Ihr Angebotspreis = Boerse + transparente Strukturkosten.'}
              </p>
            </Card>
          </Section>
        )}

        {/* So funktioniert's */}
        <Section icon={FiArrowRight} title="So funktioniert's" subtitle="In vier Schritten zu Ihrem optimalen Energievertrag">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { t: 'Angebot pruefen', d: 'Sie pruefen Ihre Preisspanne und waehlen die bevorzugte Laufzeit.' },
              { t: 'Beauftragung', d: 'Sie bestaetigen die Range und erteilen uns die Vollmacht.' },
              { t: 'Wir holen Angebote', d: 'Wir holen verbindliche Versorger-Angebote zu Ihren Konditionen ein.' },
              { t: 'Sie sparen', d: 'Wir wickeln den Wechsel ab und ueberwachen Ihren Vertrag laufend.' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border p-5">
                <div className="w-9 h-9 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mb-3">{i + 1}</div>
                <p className="font-semibold text-gray-900 text-sm">{s.t}</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Warum AIQCON */}
        <Section icon={FiAward} title="Warum AIQCON" subtitle="Ihr Vorteil als Kunde eines unabhaengigen Energiemaklers">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: FiShield, t: 'Unabhaengig & neutral', d: 'Kein Versorger — wir vergleichen den gesamten Markt fuer Sie.' },
              { icon: FiBarChart2, t: 'Echter Boersenzugang', d: 'Preise auf Basis aktueller EEX-Notierungen, voll transparent.' },
              { icon: FiActivity, t: 'Laufende Ueberwachung', d: 'Wir behalten Fristen & Marktpreise im Blick — automatisch.' },
              { icon: FiCheck, t: 'Kuendigungsservice', d: 'Wir kuendigen Ihren alten Vertrag fristgerecht fuer Sie.' },
              { icon: FiUsers, t: 'Persoenlicher Ansprechpartner', d: 'Feste Betreuung statt Hotline-Warteschleife.' },
              energiebasis
                ? { icon: FiTrendingDown, t: 'Preistransparenz', d: 'Sie sehen genau den Preis, den wir fuer Sie verhandeln — Netzentgelte und Abgaben getrennt ausgewiesen.' }
                : { icon: FiTrendingDown, t: 'Komplettpreis-Transparenz', d: 'Energie, Netz & Abgaben getrennt ausgewiesen — Sie sehen, woraus sich Ihr Preis zusammensetzt.' },
            ].map((u, i) => (
              <div key={i} className="bg-white rounded-xl border p-5">
                <u.icon className="text-teal-600 mb-2" size={22} />
                <p className="font-semibold text-gray-900 text-sm">{u.t}</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
          {/* Kennzahlen */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            {[['450+', 'Netzbetreiber'], ['100%', 'Boersentransparenz'], ['0 €', 'Kosten fuer Sie']].map(([n, l]) => (
              <div key={l} className="bg-white rounded-xl border p-5 text-center">
                <p className="text-2xl font-bold text-teal-600">{n}</p>
                <p className="text-xs text-gray-500 mt-1">{l}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* FAQ */}
        <Section icon={FiFileText} title="Haeufige Fragen">
          <FaqAccordion items={faqItems(energiebasis, istRLM)} />
        </Section>

        {/* Wiederholter CTA */}
        {options.length > 0 && (
        <div className="mt-8 bg-teal-600 rounded-xl p-6 text-center text-white">
          <h3 className="text-lg font-bold">Bereit fuer den naechsten Schritt?</h3>
          <p className="text-sm opacity-90 mt-1 mb-4">
            {energiebasis
              ? 'Bestaetigen Sie Ihre Preisspanne fuer den Energiepreis (zzgl. Netzentgelte, Abgaben und Umlagen) — wir kuemmern uns um den Rest.'
              : 'Bestaetigen Sie Ihre Preisspanne — wir kuemmern uns um den Rest.'}
          </p>
          <button onClick={() => { setConfirmError(null); setShowConfirm(true); }}
            className="inline-flex items-center gap-2 bg-white text-teal-700 font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-teal-50 transition-all">
            <FiThumbsUp size={18} /> Jetzt bestaetigen &amp; beauftragen
          </button>
        </div>
        )}

        <div className="text-center mt-8 text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} AIQCON GmbH &middot; Ihr unabhaengiger Energiemakler</p>
        </div>
      </div>

      {/* Bestaetigungs-Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Beauftragung bestaetigen</h3>
              <button onClick={() => { setShowConfirm(false); setConfirmError(null); }} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Bevorzugte Laufzeit</span><span className="font-medium">{Math.round((effectiveLaufzeit || 0) / 12)} Jahre</span></div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">{energiebasis ? 'Preisspanne Energiepreis' : 'Preisspanne'}</span>
                <span className="font-medium text-right">
                  {spanneKurz} ct/kWh
                  {energiebasis && <span className="block text-xs font-normal text-gray-500">{ZUSATZ}</span>}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-gray-500">Verbrauch</span><span className="font-medium">{verbrauch?.toLocaleString('de-DE')} kWh/a</span></div>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              placeholder="Anmerkung (optional), z.B. gewuenschter Lieferbeginn"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 mb-3" />
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mb-4">
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-1 text-teal-600" />
              <span>
                {energiebasis
                  ? 'Ich bin mit dieser Preisspanne fuer den Energiepreis einverstanden — zzgl. Netzentgelte, Abgaben und Umlagen — und beauftrage AIQCON, fuer mich verbindliche Angebote einzuholen und mich beim Wechsel zu unterstuetzen. (Unverbindlich, kostenfrei.)'
                  : 'Ich bin mit dieser Preisspanne einverstanden und beauftrage AIQCON, fuer mich verbindliche Angebote einzuholen und mich beim Wechsel zu unterstuetzen. (Unverbindlich, kostenfrei.)'}
              </span>
            </label>
            {confirmError && (
              <div role="alert" className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <FiAlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-amber-900">
                  <p className="font-medium">Ihre Beauftragung ist noch nicht bei uns eingegangen.</p>
                  <p className="mt-1">{confirmError}</p>
                  <p className="mt-1 text-xs text-amber-800">Ihre Auswahl und Ihre Anmerkung bleiben erhalten.</p>
                </div>
              </div>
            )}
            <button onClick={handleConfirm} disabled={!agree || submitting}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg font-semibold flex items-center justify-center gap-2">
              {submitting ? <FiRefreshCw className="animate-spin" size={18} /> : <FiCheck size={18} />}
              {submitting ? 'Wird gesendet...' : confirmError ? 'Erneut senden' : 'Verbindlich beauftragen'}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default KalkulationView;
